import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Swap } from '@factorialco/f0-react/icons/app'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import i18n from 'modules/core/lib/i18n'

interface IRatesSingleForm {
  areJobLevelRates?: boolean
  billRate: string
  currency: string
  disabledAllFields?: boolean
  errors: {
    billRate?: string
    minProfitMargin?: string
  }
  maxCostRate: string
  minProfitMargin: string
  marginPercentage?: string
  handleBillRateChange: (value: string) => void
  handleProfitMarginChange: (value: string) => void
}
const RatesSingleForm = ({
  areJobLevelRates = false,
  billRate,
  currency,
  disabledAllFields = false,
  errors,
  maxCostRate,
  minProfitMargin,
  marginPercentage,
  handleBillRateChange,
  handleProfitMarginChange,
}: IRatesSingleForm) => {
  const costRateLabel = areJobLevelRates
    ? {
        label: i18n.t('project_management.jobs_and_rates.sidepanels.form.max_cost_rate.label'),
        tooltip: i18n.t(
          'project_management.jobs_and_rates.sidepanels.form.max_cost_rate.tooltip_message'
        ),
      }
    : {
        label: i18n.t('project_management.people_and_rates.form.cost_rate.label'),
        tooltip: i18n.t('project_management.people_and_rates.form.cost_rate.tooltip_message'),
      }

  const markupTooltipContent = areJobLevelRates ? (
    <Box gap="s8">
      <Box gap="s4">
        <Text size="100" color="grey000White" weight="semibold">
          {i18n.t('project_management.jobs_and_rates.table.headers.markup.tooltip_title')}
        </Text>
        <Text size="100" color="grey000White">
          {i18n.t('project_management.jobs_and_rates.table.headers.markup.tooltip_message')}
        </Text>
      </Box>
      <Divider backgroundColor="#FFFFFF" />
      <Box gap="s4">
        <Text size="100" color="grey000White" weight="semibold">
          {i18n.t(
            'project_management.jobs_and_rates.table.headers.markup.tooltip_min_profit_margin_title'
          )}
        </Text>
        <Text size="100" color="grey000White">
          {i18n.t(
            'project_management.jobs_and_rates.table.headers.markup.tooltip_min_profit_margin_description'
          )}
        </Text>
      </Box>
    </Box>
  ) : (
    <Box gap="s4">
      <Text size="100" color="grey000White" weight="semibold">
        {i18n.t('project_management.people_and_rates.form.markup.tooltip_title')}
      </Text>
      <Text size="100" color="grey000White">
        {i18n.t('project_management.people_and_rates.form.markup.tooltip_message')}
      </Text>
    </Box>
  )

  const markupLabel = {
    label: areJobLevelRates
      ? i18n.t('project_management.jobs_and_rates.table.headers.markup.label')
      : i18n.t('project_management.people_and_rates.form.markup.label'),
    tooltip: markupTooltipContent,
  }

  const billRateLabel = areJobLevelRates
    ? {
        label: i18n.t('project_management.jobs_and_rates.sidepanels.form.bill_rate.label'),
        tooltip: i18n.t(
          'project_management.jobs_and_rates.sidepanels.form.bill_rate.tooltip_message_for_job'
        ),
      }
    : {
        label: i18n.t('project_management.people_and_rates.form.bill_rate.label'),
        tooltip: i18n.t('project_management.people_and_rates.form.bill_rate.tooltip_message'),
      }

  return (
    <>
      <Box flexDirection="row" gap="s8">
        <LabeledInput
          isLegend
          label={costRateLabel.label}
          secondaryLabel={<InformationTooltip information={costRateLabel.tooltip} />}
        >
          <></>
        </LabeledInput>
        <LabeledInput
          isLegend
          label={billRateLabel.label}
          secondaryLabel={<InformationTooltip information={billRateLabel.tooltip} />}
        >
          <></>
        </LabeledInput>
        <Box paddingRight="s20" />
        <LabeledInput
          isLegend
          label={markupLabel.label}
          secondaryLabel={<InformationTooltip information={markupLabel.tooltip} />}
        >
          <></>
        </LabeledInput>
      </Box>
      <Box flexDirection="row" gap="s8" alignItems="flexStart">
        <MoneyField
          hideLabel
          readonly
          label={costRateLabel.label}
          value={maxCostRate}
          currency={currency}
          maxDecimals={2}
          placeholder="0"
        />
        <MoneyField
          hideLabel
          readonly={disabledAllFields}
          label={billRateLabel.label}
          value={billRate}
          currency={currency}
          onChange={handleBillRateChange}
          error={errors.billRate}
          maxDecimals={2}
          placeholder="0"
        />
        <F0Icon icon={Swap} />
        <Box width="full">
          <MoneyField
            hideLabel
            readonly={disabledAllFields}
            label={markupLabel.label}
            value={minProfitMargin}
            currency="%"
            onChange={handleProfitMarginChange}
            maxDecimals={2}
            placeholder="0"
          />
          {marginPercentage && (
            <Box paddingTop="s4">
              <Text size="100" color="grey700">
                {areJobLevelRates
                  ? i18n.t(
                      'project_management.jobs_and_rates.sidepanels.form.min_profit_margin.label'
                    )
                  : i18n.t('project_management.people_and_rates.form.profit_margin.label')}
                {': '}
                {marginPercentage}%
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

export default RatesSingleForm
